import { useQuery, useMutation } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';

// ─── Types ──────────────────────────────────────────────────────────────────

export type LedgerEntryType =
  | 'invoice'
  | 'payment'
  | 'credit_note'
  | 'debit_note'
  | 'write_off'
  | 'discount'
  | 'advance'
  | 'adjustment';

export interface LedgerEntry {
  id: string;
  date: string;
  type: LedgerEntryType;
  description: string;
  referenceNumber: string | null;
  debit: number;
  credit: number;
  balance: number;
  relatedEntityId: string | null;
}

export interface ClientLedgerResponse {
  entries: LedgerEntry[];
  total: number;
  page: number;
  pageSize: number;
  openingBalance: number;
  closingBalance: number;
  clientId: string;
  clientName: string;
  /** Opening balance carried from last closed period (null if no closing exists). */
  openingBalanceFromClosing: number | null;
  /** Last closed period end date — entries before this are excluded. */
  lastClosedPeriodEnd: string | null;
}

export interface ClientLedgerParams {
  clientId: string;
  page?: number;
  pageSize?: number;
  dateFrom?: string;
  dateTo?: string;
  entryType?: LedgerEntryType | '';
}

// ─── Query Keys ─────────────────────────────────────────────────────────────

export const clientLedgerKeys = {
  all: ['billing', 'client-ledger'] as const,
  lists: () => [...clientLedgerKeys.all, 'list'] as const,
  list: (params: ClientLedgerParams) => [...clientLedgerKeys.lists(), params] as const,
  statement: (clientId: string) => [...clientLedgerKeys.all, 'statement', clientId] as const,
};

// ─── Hooks ──────────────────────────────────────────────────────────────────

/**
 * Fetch paginated client billing ledger with date range and entry type filters.
 * Requirements: 15.25 (client billing history as full ledger)
 */
export function useClientLedger(params: ClientLedgerParams) {
  return useQuery({
    queryKey: clientLedgerKeys.list(params),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<ClientLedgerResponse>>(
        `/billing/clients/${params.clientId}/ledger`,
        {
          params: {
            page: params.page ?? 1,
            pageSize: params.pageSize ?? 25,
            dateFrom: params.dateFrom || undefined,
            dateTo: params.dateTo || undefined,
            entryType: params.entryType || undefined,
          },
        },
      );
      return response.data.data;
    },
    enabled: !!params.clientId,
  });
}

/**
 * Download client statement as PDF.
 * Requirements: 15.25 (issue Client_Statements as downloadable PDF)
 */
export function useDownloadStatement() {
  return useMutation({
    mutationFn: async ({
      clientId,
      dateFrom,
      dateTo,
    }: {
      clientId: string;
      dateFrom?: string;
      dateTo?: string;
    }) => {
      const response = await apiClient.get(`/billing/clients/${clientId}/statement`, {
        params: {
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
        },
        responseType: 'blob',
      });
      // Trigger PDF download
      const blob = new Blob([response.data as BlobPart], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `client-statement-${clientId}-${Date.now()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
}
